import { useState, useEffect } from "react";
import { detectBrowser, BrowserType } from "../utils/browserDetection";

export interface ExtensionDetectionResult {
  isDetected: boolean;
  browser: BrowserType;
  isLoading: boolean;
  error?: string;
}

/**
 * React hook that detects if the Kairos browser extension is installed
 * Uses window.postMessage to communicate with the extension's content script
 */
export function useExtensionDetection(): ExtensionDetectionResult {
  const [state, setState] = useState<ExtensionDetectionResult>({
    isDetected: false,
    browser: "unknown",
    isLoading: true,
  });

  useEffect(() => {
    // Detect browser type
    const browserInfo = detectBrowser();

    // Skip detection for browsers without extension support (e.g., Safari)
    if (browserInfo.type === "safari" || browserInfo.type === "unknown") {
      setState({
        isDetected: false,
        browser: browserInfo.type,
        isLoading: false,
      });
      return;
    }

    // Generate unique message ID for this detection attempt
    const messageId = `kairos-detect-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Set timeout for detection (5 seconds)
    const timeout = setTimeout(() => {
      setState((prev) => {
        // Only update if still loading (no response received)
        if (prev.isLoading) {
          return {
            isDetected: false,
            browser: browserInfo.type,
            isLoading: false,
          };
        }
        return prev;
      });
    }, 5000);

    // Listen for response from extension content script
    const handleMessage = (event: MessageEvent) => {
      // Security: Validate message origin (same origin or trusted domains)
      const trustedOrigins = [
        window.location.origin,
        "https://kairos.cognixisai.com",
      ];
      const isAmplifyHostingSpa =
        /^https:\/\/[a-z0-9-]+\.[a-z0-9]+\.amplifyapp\.com$/i.test(
          event.origin,
        );

      const isTrustedOrigin =
        trustedOrigins.some(
          (origin) =>
            event.origin === origin || event.origin.startsWith(origin),
        ) || isAmplifyHostingSpa;

      if (!isTrustedOrigin) {
        return;
      }

      // Check if this is a response to our detection message
      if (
        event.data?.type === "kairos-extension-response" &&
        event.data?.messageId === messageId &&
        event.data?.success === true
      ) {
        clearTimeout(timeout);
        setState({
          isDetected: true,
          browser: browserInfo.type,
          isLoading: false,
        });
        window.removeEventListener("message", handleMessage);
      }
    };

    // Add message listener
    window.addEventListener("message", handleMessage);

    // Send detection message to extension content script
    // Use '*' as targetOrigin to allow content script to receive it
    // Content script will validate origin before responding
    window.postMessage(
      {
        type: "kairos-extension-detect",
        messageId,
        timestamp: Date.now(),
      },
      "*",
    );

    // Cleanup
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return state;
}
